import React from "react";
import { Globe2, ArrowUpRight } from "lucide-react";
import { useSite } from "../context/SiteContext";
import useReveal from "../hooks/useReveal";

export default function BronzeAgeWorld() {
  useReveal();
  const { c } = useSite();
  const d = c.bronzeAgeWorld;
  if (!d) return null;

  return (
    <section id="bronze-age" className="bg-ink py-24 sm:py-36 border-t border-[var(--c-border)]">
      <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6 text-gold">
            <Globe2 className="w-4 h-4" />
            <span className="eyebrow" data-testid="bronze-eyebrow">{d.eyebrow}</span>
          </div>
          <h2 className="font-display text-4xl sm:text-6xl font-light leading-[1.02] text-parchment">{d.title}</h2>
          <p className="mt-5 text-lg leading-relaxed text-parchment/60 font-light">{d.intro}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-14">
          {d.cards.map((card, i) => (
            <article
              key={i}
              data-testid={`bronze-card-${i}`}
              className="reveal border border-[var(--c-border)] bg-ink2 overflow-hidden flex flex-col"
              style={{ animationDelay: `${i * 90}ms` }}
            >
              <figure className="img-zoom relative h-64 sm:h-80">
                <img src={card.image} alt={card.heading} className="w-full h-full object-cover" />
                {card.credit && (
                  <figcaption className="absolute bottom-0 inset-x-0 bg-ink/85 px-4 py-2.5">
                    <span className="font-label text-[0.5rem] uppercase tracking-[0.14em] text-parchment/75">{card.credit}</span>
                  </figcaption>
                )}
              </figure>
              <div className="p-7 flex-1">
                <h3 className="font-display text-2xl sm:text-3xl font-light text-parchment leading-tight">{card.heading}</h3>
                <p className="mt-4 text-[1.02rem] leading-relaxed text-parchment/65 font-light">{card.body}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 border-t border-[var(--c-border)] pt-8">
          <p className="text-sm text-parchment/45 font-light leading-relaxed max-w-3xl italic">{d.note}</p>
          <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2">
            <span className="font-label text-[0.56rem] uppercase tracking-[0.18em] text-gold self-center">{d.sourcesLabel}</span>
            {d.sources.map((s, i) => (
              <a
                key={i}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-parchment/70 hover:text-gold transition-colors font-light"
              >
                {s.label}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
